/* eslint-disable react-hooks/exhaustive-deps */
import AutomCompleteBar from '@efex-components/searchBar/AutoCompleteBar';
import onboardingSvc from '@efex-services/onboarding';
import useBoolean from '@main-hooks/useBoolean';
import useDebounce from '@main-hooks/useDebounce';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { Typography } from '@mui/material';
import clsx from 'clsx';
import { useEffect, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiCall } from 'use-api-call';

interface CompanySectorAutocompleteProps {
  className?: string;
  data: any;
  selectedValue: ICompanySector | null | undefined;
  showCompanySectorError?: boolean;
  onSelectCompanySector: (companySector?: ICompanySector) => void;
  isError?: boolean;
}

const highlightMatch = (text: string, query: string): ReactNode => {
  if (!query.trim()) return text;

  try {
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escapedQuery})`, 'gi');

    const parts = text.split(regex);

    return parts.map((part) => {
      if (part.toLowerCase() === query.toLowerCase()) {
        const uniqueKey = `match-${part}-${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 9)}`;

        return <b key={uniqueKey}>{part}</b>;
      }

      return part;
    });
  } catch (e) {
    return text;
  }
};

const CompanySectorAutocomplete = ({
  className,
  data,
  selectedValue,
  showCompanySectorError = false,
  onSelectCompanySector,
  isError = false,
}: CompanySectorAutocompleteProps) => {
  const { t } = useTranslation();
  const [displayData, setDisplayData] = useState<ICompanySector[]>([]);
  const [searchCriteria, setSearchCriteria] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const debouncedSearchCriteria = useDebounce<string>(searchCriteria);
  const [isEmptyValue, { setTrue: isEmpty, setFalse: isNotEmpty }] =
    useBoolean(false);
  const [isInitialLoad, setIsInitialLoad] = useState(false);

  const { companySector: prefilledCompanySector, businessSector } = data;

  const {
    loading,
    data: companySector,
    invoke: getCompanySector,
  } = useApiCall((search: string) =>
    onboardingSvc.getCompanySector({
      searchCriteria: search,
    })
  );

  const handleChange = (_: any, companySectorOption: ICompanySector) => {
    if (!companySectorOption) return;
    const currentClient = displayData.find(
      (option) => option.id === companySectorOption.id
    );
    if (currentClient) {
      onSelectCompanySector(currentClient);
    }
  };

  const handleChangeCapture = async (event: any) => {
    setInputValue(event.target.value);
    const { value } = event.target;
    if (value.length >= 2) {
      setIsInitialLoad(false);
      setSearchCriteria(value);
    } else {
      onSelectCompanySector(undefined);
    }
  };

  const validateValue = () => {
    if (!selectedValue?.id) {
      isEmpty();
    } else {
      isNotEmpty();
    }
  };

  const searchData = async (search: string) => {
    if (search.length) {
      getCompanySector(search);
    } else {
      setDisplayData([]);
    }
  };

  const handleFocus = () => {
    setIsInitialLoad(true);
    getCompanySector('');
  };

  useEffect(() => {
    searchData(debouncedSearchCriteria);
  }, [debouncedSearchCriteria]);

  useEffect(() => {
    if (!companySector) return;

    const sectorData = [...companySector];
    setDisplayData(sectorData);
  }, [companySector, isInitialLoad]);

  useEffect(() => {
    if (prefilledCompanySector?.economicActivity) {
      setInputValue(prefilledCompanySector.economicActivity);
    }
  }, [prefilledCompanySector]);

  useEffect(() => {
    if (businessSector?.name) {
      setInputValue(businessSector.name);
    }
  }, [businessSector]);

  useEffect(() => {
    if (showCompanySectorError) {
      isEmpty();
    } else {
      isNotEmpty();
    }
  }, [showCompanySectorError]);

  return (
    <AutomCompleteBar
      error={isEmptyValue}
      loading={loading}
      data={displayData}
      className={clsx(
        className,
        'mb-2 !text-sm !rounded-md',
        isError && '!border-red-500 border-2'
      )}
      inputClassName="!rounded-md border-slate-300 hover:border-black focus:border-black"
      onGetOptionLabel={(companySectorOption: ICompanySector) =>
        companySectorOption.economicActivity
      }
      renderOption={(companySectorOption: ICompanySector) => (
        <div className="w-full">
          {isInitialLoad && companySectorOption === displayData[0] && (
            <div className="text-sm font-semibold border-b bg-white border-gray-300 mb-2 -mx-4 -mt-2 pt-4">
              <Typography className="pl-4" variant="body2">
                {t('mostUsed')}
              </Typography>
            </div>
          )}

          <div className="flex space-x-2 items-center">
            <p>
              {companySectorOption.economicActivityKey} -{' '}
              {highlightMatch(companySectorOption.economicActivity, inputValue)}
            </p>
          </div>
        </div>
      )}
      inputParams={(params) => {
        if (params.inputProps?.value) {
          setInputValue(params.inputProps?.value as string);
        }

        return {
          value: inputValue,
          placeholder: t('ergMxSearchByKeyWord'),
        };
      }}
      showDropdownArrow
      onFocus={handleFocus}
      onChange={handleChange}
      onChangeCapture={handleChangeCapture}
      onInputChange={handleChange}
      onBlur={validateValue}
      emptyState={
        <div className="text-center">
          <SearchOffIcon sx={{ width: 50, height: 50 }} />
          <p className="text-sm m-0">{t('textSearchNotFound')}</p>
        </div>
      }
    />
  );
};

export default CompanySectorAutocomplete;
